import React from "react";
import { useCart } from "../cart";
import { useI18n } from "../i18n";
import { X, Plus, Minus, Trash2, ExternalLink } from "lucide-react";
import { api } from "../api";

export default function CartDrawer() {
  const { items, remove, updateQty, subtotal, open, setOpen, clear } = useCart();
  const { t, lang } = useI18n();
  const [loading, setLoading] = React.useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/sumup", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size,
        })),
      });
      // Redirect to SumUp hosted checkout
      window.location.href = data.hosted_checkout_url;
    } catch (e) {
      console.error(e);
      alert(lang === "fr" ? "Erreur, réessayez." : "Error, please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {open && (
        <div data-testid="cart-overlay" onClick={() => setOpen(false)}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" />
      )}
      <aside data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-[#0a0707] border-l border-white/10 transform transition-transform duration-500 ease-out flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}>
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xs font-bold tracking-[0.25em] uppercase">{t.cart.title}</h2>
          <button data-testid="close-cart-btn" onClick={() => setOpen(false)} className="hover:text-bone">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 && (
            <p className="text-white/50 text-sm pt-12 text-center">{t.cart.empty}</p>
          )}
          {items.map((it) => (
            <div key={it.key} data-testid={`cart-item-${it.product_id}`} className="flex gap-4 border-b border-white/5 pb-6">
              <img src={it.image} alt={it.name} className="w-20 h-24 object-cover" />
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="font-bold text-sm">{lang === "fr" ? it.name_fr : it.name}</div>
                  {it.size && <div className="text-xs text-white/40 mt-1">{t.product.size}: {it.size}</div>}
                  <div className="text-xs mt-1 text-bone">€{it.price.toFixed(2)}</div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-white/20">
                    <button data-testid={`cart-decrement-${it.product_id}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="p-1 hover:bg-white/10">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="px-3 text-xs">{it.quantity}</span>
                    <button data-testid={`cart-increment-${it.product_id}`} onClick={() => updateQty(it.key, it.quantity + 1)} className="p-1 hover:bg-white/10">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.key)} className="text-white/40 hover:text-blood">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-6 border-t border-white/10 bg-[#0a0707]">
          <div className="flex justify-between mb-4 text-sm">
            <span className="text-white/60 tracking-[0.2em] uppercase text-xs">{t.cart.subtotal}</span>
            <span data-testid="cart-subtotal" className="font-bold">€{subtotal.toFixed(2)}</span>
          </div>
          <button data-testid="checkout-btn" disabled={items.length === 0 || loading} onClick={checkout}
            className="w-full bg-blood text-white py-4 text-xs font-bold tracking-[0.2em] uppercase hover:bg-blood-deep transition-colors disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
            {loading ? "..." : t.cart.checkout}
            {!loading && <ExternalLink className="w-3 h-3" />}
          </button>
          <p className="text-[10px] text-white/30 text-center mt-3 tracking-wide uppercase">
            {lang === "fr" ? "Paiement sécurisé SumUp" : "Secure checkout by SumUp"}
          </p>
        </div>
      </aside>
    </>
  );
}
